import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { ChartContainer, ChartTooltip } from "@/components/ui/chart";
import { useDarkTheme } from "@/contexts/DarkTheme";
import {
  DP02,
  fetchColor,
  formatDate,
  formatEpochDate,
  minFixedDecimal,
  toNumberOrNull,
} from "@/helpers/helperFunctions";
import { AccentColor } from "@/helpers/types";
import { useMemo } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ReferenceLine,
  XAxis,
} from "recharts";
import {
  NameType,
  ValueType,
} from "recharts/types/component/DefaultTooltipContent";
import { ContentType as TooltipContentType } from "recharts/types/component/Tooltip";
import React from "react";

const MonthlyStrategyPerformance: React.FC<{
  isProfile?: boolean;
  t: any;
  monthlyData: any[];
}> = ({ isProfile = false, t, monthlyData }) => {
  const { darkTheme } = useDarkTheme();
  const colors = useMemo(() => {
    if (monthlyData?.length) {
      return fetchColor({
        positive: AccentColor.GREEN,
        negative: AccentColor.RED,
      });
    } else {
      return {
        positive: AccentColor.GREEN,
        negative: "hsl(var(--accent))",
      };
    }
  }, [monthlyData]);
  const getRandomReturnValue = () => {
    return parseFloat((Math.random() * 100).toFixed(2));
  };

  const getPreviousDates = (days: number) => {
    const dates = [];
    const today = new Date();

    for (let i = 0; i < days; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() - i);
      dates.push(date.toISOString().split("T")[0]);
    }

    return dates;
  };

  const previous100Dates = getPreviousDates(60)?.reverse();

  const data = useMemo(() => {
    const _data: { date: string; returns: number; ts: any }[] = [];

    if (monthlyData?.length) {
      for (let i = 0; i < monthlyData?.length; i++) {
        _data.push({
          date: formatEpochDate(monthlyData?.[i]?.trade_closed_at, "DD-MMM-YY"),
          returns: monthlyData?.[i]?.actual_result,
          ts: formatEpochDate(
            monthlyData?.[i]?.trade_closed_at,
            "DD-MMM-YY HH:mm"
          ),
        });
      }

      return _data;
    } else {
      for (let i = 0; i < previous100Dates?.length; i++) {
        _data.push({
          date: formatDate(previous100Dates[i], "DD-MMM-YY"),
          returns: getRandomReturnValue(),
          ts: formatDate(previous100Dates[i], "DD-MMM-YY HH:mm"),
        });
      }

      return _data;
    }
  }, [darkTheme, monthlyData]);

  const CustomTooltip: TooltipContentType<ValueType, NameType> = ({
    active,
    payload,
  }) => {
    if (active && payload && payload.length) {
      const val = toNumberOrNull(payload[0].value);
      return (
        <div className="flex flex-col gap-2 text-sm bg-background text-foreground border rounded shadow-sm p-2">
          <div className="flex gap-1">
            {t("Trade Result")}:
            <span
              className={
                val == null
                  ? ""
                  : val < 0
                  ? "text-red-foreground"
                  : "text-green-foreground"
              }
            >
              ${minFixedDecimal(payload[0].value as number, DP02, "N/A")}
            </span>
          </div>
          <div className="">{payload[0]?.payload?.ts}</div>
        </div>
      );
    }

    return null;
  };

  return (
    <Card className={isProfile ? "w-full h-80" : "!grow h-full w-full"}>
      <CardHeader
        className={
          isProfile ? "" : "py-2 text-sm font-bold tracking-tight w-full"
        }
      >
        <div
          className={
            isProfile
              ? "text-2xl font-semibold leading-none tracking-tight"
              : "text-sm text-muted-foreground"
          }
        >
          {t("Last 30 Trades")}
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex w-full">
          <ChartContainer
            config={{ returns: { label: t("Monthly returns") } }}
            className={`aspect-auto w-full ${
              isProfile ? "h-[240px]" : "h-[150px]"
            }`}
          >
            <BarChart data={data}>
              <ReferenceLine y={0} stroke="hsl(var(--foreground))" />
              <CartesianGrid vertical={false} />
              <XAxis
                dataKey="date" // Corrected the dataKey here
                tickLine={false}
                axisLine={false}
                tickMargin={8}
              />
              {/* <YAxis
                dataKey="returns"
                tickLine={false}
                axisLine={true}
                tickMargin={8}
                tickFormatter={(value) =>
                  "$" + formatToDecimalPlaces(value, 2)
                }
              /> */}
              <ChartTooltip content={CustomTooltip} />
              <Bar dataKey="returns" type="linear">
                {data.map((entry, index) => {
                  const val = toNumberOrNull(entry.returns, 0);
                  return (
                    <Cell
                      key={`cell-${index}`}
                      fill={
                        val < 0
                          ? colors.negative
                          : val > 0
                          ? colors.positive
                          : ""
                      }
                    />
                  );
                })}
              </Bar>
            </BarChart>
          </ChartContainer>
        </div>
      </CardContent>
    </Card>
  );
};

export default MonthlyStrategyPerformance;
